import React, { type ReactNode } from 'react';
import { StyleSheet, type StyleProp, type ViewStyle } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import { Stack } from '@/components/layout';
import { createStyles, useScaling } from '@/hooks';

/** Light-blue callout gradient shared by the notice / add-child / invite cards. */
export const CALLOUT_GRADIENT = ['#F6F8FC', '#E8F1FF'];
/** Thin blue border that frames the callout. */
export const CALLOUT_BORDER = '#9CC0FE';

type OnboardingCalloutProps = {
  /** Row content — typically a leading icon, a flexed text block, and an optional trailing action. */
  children: ReactNode;
  /** Cross-axis alignment of the row: `flex-start` for tall text, `center` when a trailing button. */
  align?: 'flex-start' | 'center';
  /** Gap between the row items (defaults to 8). */
  gap?: number;
  /** Override the background gradient (defaults to the light-blue callout gradient). */
  gradient?: string[];
  /** Override the border colour. */
  borderColor?: string;
  style?: StyleProp<ViewStyle>;
};

/**
 * Rounded, light-blue gradient card with a thin blue border — the shared container
 * behind the step-1 notice, the "Add your child" prompt and the "Invite co-guardian"
 * card. Only the frame + inner row padding are shared; callers compose whatever
 * icon / text / action goes inside.
 */
export function OnboardingCallout({
  children,
  align = 'flex-start',
  gap,
  gradient = CALLOUT_GRADIENT,
  borderColor = CALLOUT_BORDER,
  style,
}: OnboardingCalloutProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <LinearGradient
      colors={gradient}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={[styles.container, { borderColor }, style]}>
      <Stack
        direction="row"
        align={align}
        gap={gap ?? scale(8)}
        paddingX={scale(8)}
        paddingY={scale(12)}>
        {children}
      </Stack>
    </LinearGradient>
  );
}

const useStyles = createStyles(({ scale }) => ({
  container: {
    borderRadius: scale(12),
    borderWidth: StyleSheet.hairlineWidth,
  },
}));

export default OnboardingCallout;
